import type { SceneHost, SceneObject } from "../SceneHost";
import { bboxFromPointerDrag } from "../geometry/shapePaths";
import { nextZIndex } from "../zIndex";
import { frameAtPoint, applyFrameContext } from "../frame/frameContext";
import { isBoardEditorChrome, isBoardWidgetChrome } from "./boardWidgetChrome";

const SVG_NS = "http://www.w3.org/2000/svg";
const DEFAULT_W = 640;
const DEFAULT_H = 400;
const CLICK_SLOP = 6;

export type FrameToolBinding = {
  destroy: () => void;
};

function createFramePreviewSvg() {
  const root = document.createElementNS(SVG_NS, "svg");
  root.setAttribute("class", "wb-frame-preview");
  root.style.position = "absolute";
  root.style.left = "0";
  root.style.top = "0";
  root.style.width = "100%";
  root.style.height = "100%";
  root.style.overflow = "visible";
  root.style.pointerEvents = "none";

  const rect = document.createElementNS(SVG_NS, "rect");
  rect.setAttribute("class", "wb-frame-preview__rect");
  rect.setAttribute("rx", "8");
  rect.setAttribute("ry", "8");
  root.appendChild(rect);

  return {
    root,
    setBbox(x: number, y: number, w: number, h: number) {
      root.style.left = `${x}px`;
      root.style.top = `${y}px`;
      root.style.width = `${w}px`;
      root.style.height = `${h}px`;
      root.setAttribute("viewBox", `0 0 ${w} ${h}`);
      rect.setAttribute("x", "0");
      rect.setAttribute("y", "0");
      rect.setAttribute("width", String(w));
      rect.setAttribute("height", String(h));
    },
  };
}

export function bindFrameTool(host: SceneHost): FrameToolBinding {
  let drawing = false;
  let start: { x: number; y: number } | null = null;
  let lastShift = false;
  let lastAlt = false;
  const preview = createFramePreviewSvg();

  function clearPreview() {
    preview.root.remove();
  }

  function updatePreview(end: { x: number; y: number }) {
    if (!start) return;
    const bbox = bboxFromPointerDrag(start, end, lastShift, lastAlt);
    if (!bbox) {
      preview.root.remove();
      return;
    }
    if (!preview.root.parentElement) {
      host.selectionOverlay.appendChild(preview.root);
    }
    preview.setBbox(bbox.x, bbox.y, bbox.w, bbox.h);
  }

  function createFrame(x: number, y: number, w: number, h: number) {
    const draft: SceneObject = {
      id: crypto.randomUUID(),
      type: "frame",
      x: Math.round(x),
      y: Math.round(y),
      w: Math.round(w),
      h: Math.round(h),
      title: "Рамка",
      zIndex: nextZIndex(host.getObjects()),
    };
    // Рамка не вкладывается в другую рамку в v1
    void host.addObject(draft, `frame-${draft.id}`).then(() => {
      host.setSelection([draft.id]);
      host.setActiveTool?.("select");
    });
  }

  function onPointerDown(ev: PointerEvent) {
    if (!host.canEdit || host.getActiveTool() !== "frame") return;
    if (host.isInteractionBlocked()) return;
    if (isBoardEditorChrome(ev.target)) return;
    if (isBoardWidgetChrome(ev.target)) return;
    if (ev.pointerType === "mouse" && ev.button !== 0) return;
    ev.preventDefault();

    const pt = host.stagePoint(ev.clientX, ev.clientY);
    drawing = true;
    start = { x: pt.x, y: pt.y };
    lastShift = ev.shiftKey;
    lastAlt = ev.altKey;
    updatePreview(pt);

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerCancel);
  }

  function onPointerMove(ev: PointerEvent) {
    if (!drawing || !start) return;
    lastShift = ev.shiftKey;
    lastAlt = ev.altKey;
    updatePreview(host.stagePoint(ev.clientX, ev.clientY));
  }

  function endDrawing(commit: boolean, ev?: PointerEvent) {
    if (!drawing) return;
    drawing = false;
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
    document.removeEventListener("pointercancel", onPointerCancel);
    clearPreview();

    const anchor = start;
    start = null;
    if (!commit || !anchor || !ev) return;

    const pt = host.stagePoint(ev.clientX, ev.clientY);
    const dx = Math.abs(pt.x - anchor.x);
    const dy = Math.abs(pt.y - anchor.y);

    if (dx < CLICK_SLOP && dy < CLICK_SLOP) {
      createFrame(anchor.x - DEFAULT_W / 2, anchor.y - DEFAULT_H / 2, DEFAULT_W, DEFAULT_H);
      return;
    }

    const bbox = bboxFromPointerDrag(anchor, pt, ev.shiftKey, ev.altKey);
    if (!bbox) {
      createFrame(anchor.x - DEFAULT_W / 2, anchor.y - DEFAULT_H / 2, DEFAULT_W, DEFAULT_H);
      return;
    }
    createFrame(bbox.x, bbox.y, bbox.w, bbox.h);
  }

  function onPointerUp(ev: PointerEvent) {
    endDrawing(true, ev);
  }

  function onPointerCancel() {
    endDrawing(false);
  }

  host.hitSurface.addEventListener("pointerdown", onPointerDown);

  return {
    destroy() {
      endDrawing(false);
      host.hitSurface.removeEventListener("pointerdown", onPointerDown);
    },
  };
}

/** Хелпер для других инструментов: проставить frameId при создании. */
export function stampFrameIdOnCreate(host: SceneHost, draft: SceneObject, cx: number, cy: number) {
  applyFrameContext(draft, frameAtPoint(host.getObjects(), cx, cy));
}
